import {useCallback, useEffect, useRef, useState} from "react";

interface UseScrollProgressResult {
    contentRef: React.RefObject<HTMLDivElement>;
    handleScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    scrollToBottom: () => void;
    hasScrolledToBottom: boolean;
    progress: number;
}

/**
 * Tracks scroll progress and whether the user has reached the bottom of a scrollable container.
 * Resets automatically when `isOpen` changes to true.
 */
const useScrollProgress = (isOpen: boolean): UseScrollProgressResult => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const rafRef = useRef<number | undefined>(undefined);
    const progressRef = useRef<number>(0);
    // Ref mirror of hasScrolledToBottom so handleScroll never recreates due to stale closure
    const hasScrolledToBottomRef = useRef(false);

    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [progress, setProgress] = useState(0);

    // Reset state when modal opens and detect content with no scrollbar
    useEffect(() => {
        if (!isOpen) return;

        setHasScrolledToBottom(false);
        hasScrolledToBottomRef.current = false;
        setProgress(0);
        progressRef.current = 0;

        const detectScroll = setTimeout(() => {
            if (contentRef.current) {
                const hasScroll =
                    contentRef.current.scrollHeight > contentRef.current.clientHeight;

                if (!hasScroll) {
                    hasScrolledToBottomRef.current = true;
                    setHasScrolledToBottom(true);
                    setProgress(100);
                    progressRef.current = 100;
                }
            }
        }, 100);

        return () => {
            clearTimeout(detectScroll);
            if (rafRef.current !== undefined) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [isOpen]);

    // Stable scroll handler — uses refs to avoid re-creation on state changes
    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const element = e.currentTarget;

        if (rafRef.current !== undefined) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            const scrollTop = element.scrollTop;
            const scrollHeight = element.scrollHeight - element.clientHeight;

            const currentProgress =
                scrollHeight > 0
                    ? Math.min((scrollTop / scrollHeight) * 100, 100)
                    : 100;

            if (Math.abs(currentProgress - progressRef.current) > 0.5) {
                progressRef.current = currentProgress;
                setProgress(currentProgress);
            }

            const isAtBottom =
                element.scrollHeight - element.scrollTop <= element.clientHeight + 40;

            if (isAtBottom && !hasScrolledToBottomRef.current) {
                hasScrolledToBottomRef.current = true;
                setHasScrolledToBottom(true);
            }
        });
    }, []);

    const scrollToBottom = useCallback(() => {
        if (!contentRef.current) return;
        contentRef.current.scrollTo({
            top: contentRef.current.scrollHeight,
            behavior: "smooth",
        });
    }, []);

    return {contentRef, handleScroll, scrollToBottom, hasScrolledToBottom, progress};
};

export default useScrollProgress;
